'use client'

import { useEffect, useMemo, useRef, useState } from 'react'

import { Button } from 'antd'
import { assetsActions, copyAssetsFromPersonalCloudThunk, copyAssetsThunk, moveAssetsThunk } from 'features/assets/model/slice/assetsSlice'
import { allBoardsSelector, getFilteredBoardsSelector } from 'features/boards/model/selectors/boardsSelectors'
import { boardsActions, copyBoardsThunk, moveBoardsThunk } from 'features/boards/model/slice/boardsSlice'
import { BoardToolbarAction } from 'shared/constants/toolbar'
import { selectModalContext, selectModalData, selectModalType } from 'features/common/model/selectors/modalSelectors'
import { closeModal } from 'features/common/model/slice/moadlSlice'
import { rootBoardIdSelector , getUserRole } from 'features/user/model/selectors/userSelectors'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { CaretDownFilled, CheckOutlined, FolderOutlined, Flex, PlusCircleOutlined } from 'shared/ui'
import { BaseModal } from 'shared/ui/BaseModal'
import { getTreeLikeSelectorFromNodes } from 'shared/utils/filesUtils'
import styled from 'styled-components'
import { getFilesPreviewIsPublicLinkSelector } from 'features/files-preview/model/selectors/filesPreviewSelectors'
import { addToAnotherBoardItemsPublicLinkThunk, moveItemsPublicLinkThunk, copyBoardsPublicLinkThunk } from 'features/public-link-files/model/slice/publicLinkFilesSlice'
import { getFilesPublicLinkNodeDataSelector, getCurrentBoardId as getPublicCurrentBoardId } from 'features/public-link-files/selectors/publicLinkFilesSelectors'
import { hasAccess } from 'shared/utils/access'
import { MemberRole } from 'features/team/model/types/members'
import { actions, setIsAddNewBoardModalOpen } from 'features/common/model/slice/appSlice'

type TreeNode = {
  key: string
  title: string
  children?: TreeNode[]
}

export const CopyMoveItemsModal = () => {
  const dispatch = useAppDispatch()
  const isBoardInTrash = (item) => item.is_explicitly_trashed || item.is_trashed
  const allBoards = useAppSelector(getFilteredBoardsSelector).filter((item) => !isBoardInTrash(item))
  const isPublicLink = useAppSelector(getFilesPreviewIsPublicLinkSelector)
  const publicLinkBoards = useAppSelector(allBoardsSelector)
  const publicLinkData = useAppSelector(getFilesPublicLinkNodeDataSelector)
  const publicCurrentBoardId = useAppSelector(getPublicCurrentBoardId)
  const userRole = useAppSelector(getUserRole)

  const [selectedDestinationId, setSelectedDestinationId] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [lastCreatedParentId, setLastCreatedParentId] = useState<string | null>(null)

  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const rootBoardId = useAppSelector(rootBoardIdSelector)
  const modalData = useAppSelector(selectModalData)
  const modalType = useAppSelector(selectModalType)
  const modalContext = useAppSelector(selectModalContext)
  const boardsIds = (modalData?.items?.filter((item) => item.type === 'dir')?.map((item) => item.id)) || []
  const assetsIds = (modalData?.items?.filter((item) => item.type === 'link' || item.type === 'file')?.map((item) => item.id)) || []
  const isOpen = [BoardToolbarAction.Move, BoardToolbarAction.Copy, BoardToolbarAction.AddToAnotherBoard, BoardToolbarAction.SaveAiAssistantGenerated].includes(modalType)
  const isVisibleMoveTopLevelOption = modalContext === "board" && modalType === BoardToolbarAction.Move && modalData?.items?.[0]?.parent_id !== rootBoardId
  const isMove = modalType === BoardToolbarAction.Move
  const isAddToAnotherBoard = modalType === BoardToolbarAction.AddToAnotherBoard

  const getTitle = () => {
    if (modalType === BoardToolbarAction.SaveAiAssistantGenerated) {
      return 'Save generated asset'
    }

    if (isAddToAnotherBoard) {
      return 'Add asset to another board'
    }

    const isMultiple = (modalData?.items?.length || 0) > 1
    const itemType =
      modalContext === 'mixed' ? 'object'
        : modalContext === 'asset' ? 'asset'
          : 'board'
    return `${isMove ? 'Move' : 'Copy'} ${itemType}${isMultiple ? 's' : ''} to another board`
  }

  const getSubtitle = () => {
    if (isAddToAnotherBoard) {
      return 'The asset stays in place — it\'ll simply appear on additional boards.'
    }

    if (modalType === BoardToolbarAction.SaveAiAssistantGenerated) {
      return ''
    }

    const itemsCount = modalData?.items?.length || 0
    const isMultiple = itemsCount > 1

    if (modalContext === 'mixed') {
      return isMove
        ? "You're about to move the selected boards and assets to another board. They'll be removed from their current location. Select the destination to continue."
        : "You're about to copy the selected boards and assets to another board. Originals stay where they are. Choose the destination board to proceed."
    }

    if (modalContext === 'board') {
      if (isMove) {
        return isMultiple
          ? 'These boards will be moved to another location. Their contents will stay intact. Select the destination to continue.'
          : 'This board will be moved to another location. Its contents will stay intact. Select the destination to continue.'
      }

      return isMultiple
        ? 'Copies of these boards will be created in the selected location. Choose where to copy them.'
        : 'A copy of this board will be created in the selected location. Choose where to copy it.'
    }

    if (modalContext === 'asset') {
      if (isMove) {
        return isMultiple
          ? 'These assets will be moved and will no longer be available in the current board. Select where to move them.'
          : 'This asset will be moved and will no longer be available in the current board. Select where to move it.'
      }

      return isMultiple
        ? 'These assets will be copied to another board. The originals stay here. Select the destination board.'
        : 'This asset will be copied to another board. The original stays here. Select the destination board.'
    }

    return ''
  }

  const buttonText = isAddToAnotherBoard ? 'Add' : modalType === BoardToolbarAction.SaveAiAssistantGenerated ? 'Save' : (isMove ? 'Move' : 'Copy')

  const getTargetTypes = () => {
    const isBoard = modalContext === 'board' || (!modalData && (modalData?.items?.length || 0) > 0)
    const isAsset = modalContext === 'asset' || (!modalData && (modalData?.items?.length || 0) > 0)
    const isMixed = modalContext === 'mixed'
    return { isBoard, isAsset, isMixed }
  }

  const handleMoveCopySubmit = async (destinationId: string) => {
    const assetIdsCopy = [...assetsIds]
    const boardIdsCopy = [...boardsIds]
    dispatch(assetsActions.unselectAll())
    dispatch(boardsActions.unselectAll())

    if (modalType === BoardToolbarAction.SaveAiAssistantGenerated) {
      await dispatch(copyAssetsFromPersonalCloudThunk({ asset: modalData?.items[0], destinationId })).unwrap()
      handleClose()
      return
    }

    try {
      const { isBoard, isAsset, isMixed } = getTargetTypes()
      const isMoveAction = modalType === BoardToolbarAction.Move
      const isCopyAction = [BoardToolbarAction.AddToAnotherBoard, BoardToolbarAction.Copy].includes(modalType)

      if (isBoard && !isAsset && boardsIds.length > 0) {
        if (isMoveAction) await dispatch(moveBoardsThunk({ ids: boardsIds, destinationId })).unwrap()
        else if (isCopyAction) await dispatch(copyBoardsThunk({ ids: boardsIds, destinationId })).unwrap()
      } else if (isAsset && !isBoard && assetsIds.length > 0) {
        if (isMoveAction) {
          await dispatch(
            moveAssetsThunk({ ids: assetsIds, destinationId, items: modalData?.items }) // pass items
          ).unwrap()
        } else if (isCopyAction) {
          await dispatch(
            copyAssetsThunk({ ids: assetsIds, destinationId, items: modalData?.items }) // pass items
          ).unwrap()
        }
      } else if (isMixed) {
        if (isMoveAction) {
          await Promise.all([
            dispatch(moveAssetsThunk({ ids: assetsIds, destinationId, items: modalData?.items })).unwrap(), // pass items
            dispatch(moveBoardsThunk({ ids: boardsIds, destinationId })).unwrap(),
          ])
        } else if (isCopyAction) {
          await Promise.all([
            dispatch(copyAssetsThunk({ ids: assetsIds, destinationId, items: modalData?.items })).unwrap(), // pass items
            dispatch(copyBoardsThunk({ ids: boardsIds, destinationId })).unwrap(),
          ])
        }
      }
      handleClose()
    } catch (error) {
      dispatch(assetsActions.setSelectedItemsIds(assetIdsCopy))
      dispatch(boardsActions.setSelectedItemsIds(boardIdsCopy))
    }
  }

  const handleMoveCopySubmitPublicLink = (destinationId: string) => {
    const { isBoard, isAsset, isMixed } = getTargetTypes()
    const isMoveAction = modalType === BoardToolbarAction.Move
    const isCopyAction = [BoardToolbarAction.AddToAnotherBoard, BoardToolbarAction.Copy].includes(modalType)

    if (isBoard) {
      if (isMoveAction) dispatch(moveItemsPublicLinkThunk({ destinationId }))
      else if (isCopyAction) dispatch(copyBoardsPublicLinkThunk({ destinationId }))
    }
    if (isAsset) {
      if (isMoveAction) dispatch(moveItemsPublicLinkThunk({ destinationId }))
      else if (isCopyAction) dispatch(addToAnotherBoardItemsPublicLinkThunk({ destinationId }))
    }
    if (isMixed) {
      if (isMoveAction) dispatch(moveItemsPublicLinkThunk({ destinationId }))
      else if (isCopyAction) {
        // Сначала скопируем борды, затем ассеты
        dispatch(copyBoardsPublicLinkThunk({ destinationId }))
        dispatch(addToAnotherBoardItemsPublicLinkThunk({ destinationId }))
      }
    }
    handleClose()
  }

  const handleSubmit = () => {
    if (selectedDestinationId) {
      if (isPublicLink) {
        handleMoveCopySubmitPublicLink(selectedDestinationId)
      } else {
        handleMoveCopySubmit(selectedDestinationId)
      }
      setSelectedDestinationId(null)
    }
  }

  const handleClose = () => {
    setSelectedDestinationId(null)
    dispatch(closeModal())
  }

  const treeData: TreeNode[] = useMemo(() => {
    const nodes = isPublicLink
      ? [...publicLinkBoards, { id: 'root', type: 'dir', name: publicLinkData?.name }]
      : allBoards || []
    return (getTreeLikeSelectorFromNodes(nodes as any, []) as any) as TreeNode[]
  }, [isPublicLink, publicLinkBoards, publicLinkData?.name, allBoards])

  const sourceBoards = useMemo(
    () => (isPublicLink ? publicLinkBoards : allBoards) || [],
    [isPublicLink, publicLinkBoards, allBoards]
  )

  const byId = useMemo(() => {
    const m = new Map<string, any>()
    sourceBoards.forEach(b => m.set(String(b.id), b))
    return m
  }, [sourceBoards])

  const childrenMap = useMemo(() => {
    const m = new Map<string, string[]>()
    sourceBoards.forEach(b => {
      const pid = String(b.parent_id ?? '')
      const arr = m.get(pid) || []
      arr.push(String(b.id))
      m.set(pid, arr)
    })
    return m
  }, [sourceBoards])

  const collectDescendants = (id: string) => {
    const out: string[] = []
    const stack = [id]
    while (stack.length) {
      const cur = stack.pop()!
      const kids = childrenMap.get(cur) || []
      out.push(...kids)
      stack.push(...kids)
    }
    return out
  }

  const disabledKeys = useMemo(() => {
    const s = new Set<string>()
    boardsIds.forEach((rawId) => {
      const id = String(rawId)
      s.add(id)

      const parentId = byId.get(id)?.parent_id
      if (parentId) s.add(String(parentId))

      const desc = collectDescendants(id)
      desc.forEach(d => s.add(d))
    })
    if (isPublicLink) {
      const currentKey = publicCurrentBoardId ? String(publicCurrentBoardId) : 'root'
      s.add(currentKey)
    }
    return s
  }, [boardsIds, byId, childrenMap, isPublicLink, publicCurrentBoardId])

  const handleAddBoardRoot = () => {
    setLastCreatedParentId(null)
    dispatch(actions.setModalBoardData({ boardIds: null, boards: null, parentId: null }))
    dispatch(setIsAddNewBoardModalOpen(true))
    dispatch(boardsActions.setSelectedParentId(null))
  }

  const toggleExpand = (key: string) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const expandAncestors = (id: string) => {
    let cur = byId.get(String(id))
    const toExpand: Record<string, boolean> = {}
    while (cur && cur.parent_id != null && cur.parent_id !== undefined) {
      const pid = String(cur.parent_id)
      toExpand[pid] = true
      cur = byId.get(pid)
    }
    setExpanded(prev => ({ ...prev, ...toExpand }))
  }

  const prevIdsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    const currIds = new Set<string>()
    sourceBoards.forEach(b => currIds.add(String(b.id)))

    if (prevIdsRef.current.size === 0) {
      prevIdsRef.current = currIds
      return
    }

    const newIds: string[] = []
    currIds.forEach(id => {
      if (!prevIdsRef.current.has(id)) newIds.push(id)
    })

    if (newIds.length > 0) {
      let candidate: any | null = null

      if (lastCreatedParentId !== undefined) {
        for (let i = sourceBoards.length - 1; i >= 0; i--) {
          const b = sourceBoards[i]
          const id = String(b.id)
          if (newIds.includes(id) && String(b.parent_id ?? '') === String(lastCreatedParentId ?? '')) {
            candidate = b
            break
          }
        }
      }

      if (!candidate) {
        for (let i = sourceBoards.length - 1; i >= 0; i--) {
          const b = sourceBoards[i]
          const id = String(b.id)
          if (newIds.includes(id)) {
            candidate = b
            break
          }
        }
      }

      if (candidate) {
        const candId = String(candidate.id)

        expandAncestors(candId)

        if (!disabledKeys.has(candId)) {
          setSelectedDestinationId(candId)
        }
      }

      setLastCreatedParentId(null)
    }

    prevIdsRef.current = currIds
  }, [sourceBoards, disabledKeys, lastCreatedParentId, byId])


  useEffect(() => {
    if (selectedDestinationId && itemRefs.current[selectedDestinationId]) {
      itemRefs.current[selectedDestinationId]!.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      })
    }
  }, [selectedDestinationId])

  const renderNode = (node: TreeNode, level = 1) => {
    const key = String(node.key)
    const hasChildren = !!node.children && node.children.length > 0
    const isExpanded = !!expanded[key]
    const isSelected = selectedDestinationId === key
    const isDisabled = disabledKeys.has(key)

    return (
      <div key={key}>
        <SItem
          ref={(el) => { itemRefs.current[key] = el }}
          level={level}
          disabled={isDisabled}
          className={isSelected ? 'active-item' : ''}
          onClick={() => {
            if (!isDisabled) setSelectedDestinationId(key)
          }}
        >
          <div className="left">
            {hasChildren ? (
              <div
                className="expand-icon"
                onClick={(e) => { e.stopPropagation(); toggleExpand(key) }}
              >
                <CaretDownFilled className={isExpanded ? 'icon-normal' : 'icon-rotate'} />
              </div>
            ) : (
              <div className="expand-icon-placeholder" />
            )}

            <span className="label"
              title={node.title}
            >
              {node.title}
            </span>
          </div>

          <div className="actions"
            style={{ visibility: isDisabled ? 'hidden' : 'visible' }}
          >
            {hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && !isPublicLink && (
              <PlusCircleOutlined
                className="action-icon"
                onClick={(e) => {
                  e.stopPropagation()
                  setLastCreatedParentId(key)
                  dispatch(actions.setModalBoardData({ boardIds: null, boards: null, parentId: key }))
                  dispatch(setIsAddNewBoardModalOpen(true))
                  dispatch(boardsActions.setSelectedParentId(key))
                }}
              />
            )}
          </div>
        </SItem>

        {hasChildren && (
          <SChildren className={isExpanded ? 'expanded' : ''}>
            {node.children!.map(child => renderNode(child, level + 1))}
          </SChildren>
        )}
      </div>
    )
  }

  return (
    <BaseModal
      title={getTitle()}
      isOpen={isOpen}
      onClose={handleClose}
    >
      <SContent>
        <SModalDescription>
          {getSubtitle()}
        </SModalDescription>

        <SListContainer>
          {treeData.map(n => renderNode(n, 1))}

          {isVisibleMoveTopLevelOption && !isPublicLink && (
            <SMoveTopLevelOption
              isSelected={selectedDestinationId === rootBoardId}
              onClick={() => setSelectedDestinationId(rootBoardId)}
            >
              <span>
                <FolderOutlined style={{ marginRight: 5 }} />
                Move board to top level
              </span>

              {selectedDestinationId === rootBoardId && <CheckOutlined style={{ color: 'var(--color-primary)' }} />}
            </SMoveTopLevelOption>
          )}
        </SListContainer>

        <Flex justify={!isPublicLink ? 'space-between' : 'flex-end'}>
          {hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && !isPublicLink && (
            <SAddNewBoardButton onClick={() => {
              handleAddBoardRoot()
            }}
            >
              <PlusCircleOutlined />
              Create Board
            </SAddNewBoardButton>
          )}

          <SButtonsContainer>
            <Button size="large"
              onClick={handleClose}
            >
              Cancel
            </Button>

            <Button
              type="primary"
              size="large"
              onClick={handleSubmit}
              disabled={!selectedDestinationId}
            >
              {buttonText}
            </Button>
          </SButtonsContainer>
        </Flex>
      </SContent>
    </BaseModal>
  )
}

/* Styles */

const SContent = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const SModalDescription = styled.div`
  font-size: 14px;
  color: rgba(0, 0, 0, 0.45);
`

const SListContainer = styled.div`
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fff;
  min-height: 240px;
  max-height: 472px;
  overflow: auto;
  padding: 2px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const SChildren = styled.div`
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.2s ease-in;

  &.expanded {
    max-height: 1000px;
    overflow: visible;
  }
`

interface SItemProps { level: number; disabled?: boolean }
const SItem = styled.div<SItemProps>`
  min-height: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0px 16px 0px ${({ level }) => 24 * level}px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.3s;
  overflow: hidden;
  color: rgba(0, 0, 0, 0.88);
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  border-radius: 8px;

  &:hover { background-color: ${({ disabled }) => (disabled ? 'transparent' : 'rgba(0,0,0,0.06)')}; }
  &.active-item { background-color: rgba(0, 0, 0, 0.06); }

  .left {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    overflow: hidden;
  }

  .label {
    color: ${({ disabled }) => (disabled ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.88)')};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .actions { display: none; gap: 6px; flex-shrink: 0; }
  &:hover .actions { display: ${({ disabled }) => (disabled ? 'none' : 'flex')}; }

  .action-icon {
    font-size: 18px;
    color: rgba(0, 0, 0, 0.45);
    transition: color 0.2s ease;
  }
  .action-icon:hover { color: var(--color-primary); }

  .expand-icon, .expand-icon-placeholder {
    width: 18px; height: 18px;
    display: inline-flex; align-items: center; justify-content: center;
    color: rgba(0,0,0,0.88);
    flex-shrink: 0;
  }
  .expand-icon { cursor: pointer !important; }
  .expand-icon-placeholder { pointer-events: none; }

  .icon-rotate { transition: transform 0.2s; transform: rotate(-90deg); }
  .icon-normal { transition: transform 0.2s; transform: rotate(0); }
`

const SButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`

const SMoveTopLevelOption = styled.div<{ isSelected: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 8px;
  background: ${({ isSelected }) => (isSelected ? '#f5f5f5' : 'transparent')};
  &:hover { background: #f5f5f5; }
`

const SAddNewBoardButton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`

export default CopyMoveItemsModal
